/**
 * Main App component
 * Handles routing and layout with lazy-loaded pages
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@store/authStore';
import { ErrorBoundary } from '@components/common';

// Layouts (loaded eagerly - always needed)
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Lazy-loaded pages
const HomePage = lazy(() => import('./features/home/HomePage'));
const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const WorkoutPage = lazy(() => import('./features/workout/WorkoutPage'));
const LeaderboardPage = lazy(() => import('./features/leaderboard/LeaderboardPage'));
const ProfilePage = lazy(() => import('./features/profile/ProfilePage'));
const ChallengesPage = lazy(() => import('./features/challenges/ChallengesPage'));
const AchievementsPage = lazy(() => import('./features/achievements/AchievementsPage'));
const ChallengeDetailPage = lazy(() => import('./features/challenges/ChallengeDetailPage'));
const RunningPage = lazy(() => import('./features/running').then(m => ({ default: m.RunningPage })));
const RunningDetailPage = lazy(() => import('./features/running').then(m => ({ default: m.RunningDetailPage })));
const RunningTrackPage = lazy(() => import('./features/running').then(m => ({ default: m.RunningTrackPage })));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const FriendsPage = lazy(() => import('./features/friends/FriendsPage'));
const FriendProfilePage = lazy(() => import('./features/friends/FriendProfilePage'));
const TeamsPage = lazy(() => import('./features/teams/TeamsPage'));
const CityBattlePage = lazy(() => import('./features/city-battles/CityBattlePage'));
const QuestsPage = lazy(() => import('./features/quests/QuestsPage'));
const TeamChallengesPage = lazy(() => import('./features/team-challenges/TeamChallengesPage'));
const NotificationsPage = lazy(() => import('./features/notifications/NotificationsPage'));
const ChatPage = lazy(() => import('./features/chat/ChatPage'));
const PetPage = lazy(() => import('./features/pet/PetPage'));
const AdminDashboard = lazy(() => import('./features/admin/AdminDashboard'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>
  );
}

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Public route wrapper (redirect to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPasswordPage />
                  </PublicRoute>
                }
              />
            </Route>

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/workout" element={<WorkoutPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/challenges" element={<ChallengesPage />} />
              <Route path="/challenges/:id" element={<ChallengeDetailPage />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/:userId" element={<ProfilePage />} />
              <Route path="/running" element={<RunningPage />} />
              <Route path="/running/track" element={<RunningTrackPage />} />
              <Route path="/running/:id" element={<RunningDetailPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/friends" element={<FriendsPage />} />
              <Route path="/friends/:userId" element={<FriendProfilePage />} />
              <Route path="/teams" element={<TeamsPage />} />
              <Route path="/city-battles" element={<CityBattlePage />} />
              <Route path="/quests" element={<QuestsPage />} />
              <Route path="/team-challenges" element={<TeamChallengesPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/pet" element={<PetPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
